import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest } from '../utils/auth';
import { lockAppScroll } from '../utils/scrollLock';
import Pagination from './Pagination';
import GenerationOutcomeSummary from './GenerationOutcomeSummary';
import type { UsageData } from '../pages/GenerationUsagePage';
import './ModelUsagePanel.css';

interface Totals {
  confirmedCredits: number;
  paidCount: number;
  attempt1Credits: number;
  attempt2Credits: number;
  attempt3Credits: number;
  attempt1PaidCount: number;
  attempt2PaidCount: number;
  attempt3PaidCount: number;
  unknownAttemptCredits: number;
}
interface ModelRow {
  modelKey: string;
  modelId: string | null;
  displayName: string;
  identity: string;
  current: Totals;
  previous: Totals;
  fallbackRate: number | null;
}
interface ModelReport {
  modelRows: ModelRow[];
  modelPagination: { page: number; pages: number; total: number };
}
const number = (value: number) => value.toLocaleString('en-US');
const creditsKey = (n: number) =>
  `attempt${n}Credits` as 'attempt1Credits' | 'attempt2Credits' | 'attempt3Credits';
const countKey = (n: number) =>
  `attempt${n}PaidCount` as 'attempt1PaidCount' | 'attempt2PaidCount' | 'attempt3PaidCount';
const change = (current: number, previous: number) =>
  `${current > previous ? '+' : ''}${number(current - previous)} vs previous ${number(previous)}`;

function ModelDrawer({ model, query, onClose }: { model: ModelRow; query: string; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<UsageData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const node = dialog.current;
    node?.showModal();
    const unlock = lockAppScroll();
    return () => {
      node?.close();
      unlock();
      previousFocus?.focus();
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const params = new URLSearchParams(query);
    params.delete('view');
    params.set('modelKey', model.modelKey);
    params.set('page', String(page));
    params.set('limit', '10');
    setLoading(true);
    setError('');
    setData(null);
    apiRequest<UsageData>(`/api/admin/generation-usage?${params}`, { signal: controller.signal })
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'Unable to load model');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [model.modelKey, query, page, refresh]);
  return createPortal(
    <dialog
      ref={dialog}
      className="gu-model-dialog"
      aria-labelledby="gu-model-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="gu-model-drawer">
        <header>
          <div>
            <h2 id="gu-model-title">{model.displayName}</h2>
            <code>{model.modelId || 'Model identity unavailable'}</code>
            {model.identity === 'legacy' ? <p>Legacy provider model — variant unavailable</p> : null}
          </div>
          <button autoFocus type="button" aria-label="Close model details" onClick={onClose}>
            ×
          </button>
        </header>
        {loading ? (
          <p role="status">Loading model details…</p>
        ) : error ? (
          <p role="alert">
            {error} <button onClick={() => setRefresh((value) => value + 1)}>Retry</button>
          </p>
        ) : data ? (
          <>
            <div className="gu-model-total">
              <span>Confirmed credits</span>
              <strong>{number(data.summary.confirmedCredits)}</strong>
              <small>
                {data.previous
                  ? change(data.summary.confirmedCredits, data.previous.summary.confirmedCredits)
                  : 'No comparison available'}
              </small>
            </div>
            <h3>Credit distribution</h3>
            <div className="gu-model-distribution">
              {[1, 2, 3].map((n) => (
                <div key={n}>
                  <span>Attempt {n}</span>
                  <strong>{number(data.summary[creditsKey(n)])} credits</strong>
                  <small>{number(data.summary[countKey(n)])} charged operations</small>
                  <small>
                    {data.previous
                      ? change(data.summary[creditsKey(n)], data.previous.summary[creditsKey(n)])
                      : '—'}
                  </small>
                </div>
              ))}
            </div>
            <p className="gu-note">
              Unknown attempt: {number(data.summary.unknownAttemptCredits)} credits. Refunds and unconfirmed
              charges are excluded from credit distribution.
            </p>
            <h3>{number(data.pagination.total)} operations</h3>
            {data.rows.length ? (
              data.rows.map((row) => (
                <details className="gu-model-operation" key={row.reservationId}>
                  <summary>
                    <span>
                      {new Date(row.createdAt).toLocaleString('en-GB', { timeZone: 'Asia/Hong_Kong' })} · HKT
                    </span>
                    <strong>
                      {row.amount} credits · {row.status}
                    </strong>
                    <small>
                      {row.userName || row.userId} ·{' '}
                      {row.successfulOutcome?.provider ||
                        row.generationLog?.successfulProvider ||
                        'No successful provider recorded'}
                    </small>
                  </summary>
                  <p>
                    Reservation: <code>{row.reservationId}</code>
                  </p>
                  <GenerationOutcomeSummary row={row} />
                  {row.generationLog?.attempts.length ? (
                    <ol>
                      {row.generationLog.attempts.map((attempt, index) => (
                        <li key={index}>
                          <strong>
                            Attempt {attempt.attemptNumber} · {attempt.status}
                          </strong>
                          <p>
                            {attempt.provider} · {attempt.modelId || attempt.model} ·{' '}
                            {(attempt.durationMs / 1000).toFixed(1)}s
                          </p>
                          {attempt.errorMessage ? (
                            <p>
                              {attempt.errorCode ? `${attempt.errorCode}: ` : ''}
                              {attempt.errorMessage}
                            </p>
                          ) : null}
                          {attempt.requestParams ? (
                            <details>
                              <summary>Request parameters</summary>
                              <pre>{JSON.stringify(attempt.requestParams, null, 2)}</pre>
                            </details>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p>Detailed attempt history unavailable.</p>
                  )}
                </details>
              ))
            ) : (
              <p>No operations in this period.</p>
            )}
            <Pagination
              variant="analytics"
              currentPage={data.pagination.page}
              totalPages={data.pagination.pages}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </div>
    </dialog>,
    document.body,
  );
}

export default function ModelUsagePanel({ query }: { query: string }) {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sort, setSort] = useState('credits');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [activeOnly, setActiveOnly] = useState(true);
  const [data, setData] = useState<ModelReport | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState<ModelRow | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const params = new URLSearchParams(query);
    params.delete('page');
    params.set('view', 'models');
    params.set('modelSearch', appliedSearch);
    params.set('modelSort', sort);
    params.set('modelPage', String(page));
    params.set('modelLimit', String(limit));
    params.set('activeOnly', String(activeOnly));
    setLoading(true);
    setError('');
    apiRequest<ModelReport>(`/api/admin/generation-usage?${params}`, { signal: controller.signal })
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'Unable to load model usage');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [query, appliedSearch, sort, page, limit, activeOnly, refresh]);
  return (
    <section className="gu-model-panel" aria-labelledby="gu-model-heading">
      <h2 id="gu-model-heading">Model usage</h2>
      <p className="gu-note">
        Confirmed credits by successful attempt. Attempt 3 share = its credits ÷ total confirmed credits.
        Select a model for details.
      </p>
      <div className="gu-model-controls">
        <label>
          Search models
          <input
            aria-label="Search models"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or model ID"
          />
        </label>
        <label>
          Sort by
          <select
            aria-label="Sort models"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPage(1);
            }}
          >
            <option value="credits">Total credits</option>
            <option value="attempt3">Attempt 3 credits</option>
            <option value="fallbackRate">Attempt 3 share</option>
            <option value="name">Model name</option>
          </select>
        </label>
        <label>
          Per page
          <select
            aria-label="Models per page"
            value={limit}
            onChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </label>
        <label className="gu-model-active">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(event) => {
              setActiveOnly(event.target.checked);
              setPage(1);
            }}
          />
          Only models with credits used
        </label>
      </div>
      {loading ? (
        <p role="status">Loading model usage…</p>
      ) : error ? (
        <p role="alert">
          {error} <button onClick={() => setRefresh((value) => value + 1)}>Retry model usage</button>
        </p>
      ) : data ? (
        <>
          <p className="gu-note">{number(data.modelPagination.total)} models</p>
          {data.modelRows.length ? (
            <div
              className="gu-model-table-wrap"
              tabIndex={0}
              role="region"
              aria-label="Model usage, scroll horizontally for all columns"
            >
              <table className="gu-model-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Total credits</th>
                    {[1, 2, 3].map((n) => (
                      <th className="gu-model-attempt" key={n}>
                        Attempt {n}
                      </th>
                    ))}
                    <th>Attempt 3 share</th>
                  </tr>
                </thead>
                <tbody>
                  {data.modelRows.map((model) => (
                    <tr key={model.modelKey}>
                      <td>
                        <button className="gu-model-open" onClick={() => setSelected(model)}>
                          {model.displayName} →
                        </button>
                        <small>
                          {model.modelId}
                          {model.identity === 'legacy' ? ' · legacy' : ''}
                        </small>
                      </td>
                      <td>
                        <strong>{number(model.current.confirmedCredits)}</strong>
                        <small>{number(model.current.paidCount)} operations</small>
                        <small>
                          {change(model.current.confirmedCredits, model.previous.confirmedCredits)}
                        </small>
                        {model.current.unknownAttemptCredits ? (
                          <small className="gu-model-unknown">
                            Unknown attempt: {number(model.current.unknownAttemptCredits)}
                          </small>
                        ) : null}
                      </td>
                      {[1, 2, 3].map((n) => (
                        <td className="gu-model-attempt" key={n}>
                          <strong>{number(model.current[creditsKey(n)])}</strong>
                          <small>{number(model.current[countKey(n)])} operations</small>
                        </td>
                      ))}
                      <td>
                        {model.fallbackRate === null ? '—' : `${(model.fallbackRate * 100).toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No models match. Try another search or include models without credit usage.</p>
          )}
          <Pagination
            variant="analytics"
            currentPage={data.modelPagination.page}
            totalPages={data.modelPagination.pages}
            onPageChange={setPage}
          />
        </>
      ) : null}
      {selected ? <ModelDrawer model={selected} query={query} onClose={() => setSelected(null)} /> : null}
    </section>
  );
}
